<script setup lang="ts">
import { computed } from 'vue'
import { NDataTable, NPagination, type DataTableColumns } from 'naive-ui'
import type { Tone } from '../tokens'
import { toneVars } from '../tone-vars'
import EmptyState from './EmptyState.vue'

/**
 * 列表表格。透传 naive-ui 的 n-data-table,只固定那几件**不该由每个页面自己决定**的事:
 * 不画边框(斑马纹已经够分隔行)、表头 12px 灰、行高一致、空数据走 EmptyState。
 *
 * `columns` 与 `data` 的入参类型是 `unknown[]`(规格 §4.3 的签名),因为列定义里
 * 往往带 render 函数,类型精确写会逼调用方在这里再导一次列类型。代价是这一层
 * 不做列的类型检查 —— 列本身由各页自己写,红不红在那边看得见。
 *
 * 分页不走 n-data-table 自带的那个,而是自己渲染 n-pagination:
 * 两个端的取数都是**服务端分页**(一次 50 条),自带分页只认本地数据条数,
 * 会算出「只有 1 页」而把后面的数据藏起来。
 *
 * ═══ scrollX 为什么是 prop,而且默认不传 ═══
 *
 * 这一层原来写死 `:scroll-x="960"`,意思是「这张表至少 960 宽」。它当然让宽表排得开,
 * 但副作用是**放进任何窄于 960 的容器里都会出事**,而且出得很难看:
 * 表格不会换行,而是把最右边那一列裁在半句话上(一个「已京」)。
 * 上一轮运营端的绕法是「把表挪到整宽」—— 表挪得走,雷挪不走:
 * 下一个把表格放进抽屉、放进左栏、放进分栏卡片的人会再撞一次。
 *
 * 现在默认不传,表格随容器自适应;真需要横向滚动(列加起来放不下)的表
 * **显式**传一个数,那时滚动条是看得见的、是有意的。
 * 这个判据在浏览器里量:两端每一张表都要满足
 * `table.scrollWidth - table.clientWidth === 0`。
 */
interface PagerConfig {
  readonly page: number
  readonly pageSize: number
  readonly itemCount: number
  readonly onPage: (page: number) => void
}

const props = withDefaults(
  defineProps<{
    columns: unknown[]
    data: readonly unknown[]
    rowKey: (row: any) => string | number
    loading?: boolean
    tone?: Tone
    pagination?: false | PagerConfig
    striped?: boolean
    emptyText?: string
    /** 传了才给表格一个最小宽度(列多到放不下时才有意义);不传就是随容器自适应 */
    scrollX?: number
  }>(),
  { loading: false, tone: 'light', striped: false, emptyText: '没有数据' },
)

const vars = computed(() => toneVars(props.tone))

/**
 * 表格组件要的是可变数组、而且要求元素可作行数据,而调用方给的是只读视图 ——
 * 这里只做形状转换,不复制内容。行类型不在这一层检查:列定义(带 render 函数)由各页自己写,
 * 在那里类型是精确的,硬写在这一层只会逼调用方多导一次型。
 */
const rows = computed(() => props.data as any[])
const columns = computed(() => props.columns as DataTableColumns<any>)

function handlePage(page: number): void {
  if (props.pagination) props.pagination.onPage(page)
}
</script>

<template>
  <div class="table" :style="vars">
    <n-data-table
      :columns="columns"
      :data="rows"
      :row-key="props.rowKey"
      :loading="props.loading"
      :bordered="false"
      :single-line="false"
      :striped="props.striped"
      :scroll-x="props.scrollX"
      :pagination="false"
      size="small"
    >
      <template #empty>
        <slot name="empty">
          <EmptyState :text="props.emptyText" :tone="props.tone" />
        </slot>
      </template>
    </n-data-table>

    <div v-if="props.pagination" class="pager">
      <n-pagination
        :page="props.pagination.page"
        :page-size="props.pagination.pageSize"
        :item-count="props.pagination.itemCount"
        :page-slot="7"
        @update:page="handlePage"
      />
    </div>
  </div>
</template>

<style scoped>
.table {
  min-width: 0;
}

.pager {
  display: flex;
  justify-content: flex-end;
  padding: 14px 16px 4px;
}

/* 行高与单元格的纵向居中:表格里混着标签、等宽单号、按钮,不居中的话
   同一行的三个元素会对不齐,而那种歪斜说不上哪里错、就是不好看 */
.table :deep(.n-data-table-td) {
  height: 44px;
  vertical-align: middle;
}

.table :deep(.n-data-table-th) {
  height: 38px;
  font-size: 12px;
  font-weight: 500;
}

/* 单元格里的等宽小字(单号、ID 这类)统一走 tabular-nums,纵向才对得齐 */
.table :deep(.n-data-table-td) {
  font-variant-numeric: tabular-nums;
}
</style>
